import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import './CreatorApplicationSocialInfo.scss';

import { updateCreatorApplication } from '../../Actions/ApplicationActions';
import { isValidUrl } from '../../Helpers/formatting';

import ScrollToTop from '../../Components/General/ScrollToTop';
import Tooltip from '../../Components/General/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCheck } from '@fortawesome/pro-solid-svg-icons';

const CreatorApplicationSocialInfo = props => {
  const application = props.application.creatorApplication;

  const isValidSocialMediaHandle = value => {
    const regex = /^[a-zA-Z0-9._]+$/;
    return regex.test(value);
  };

  // Because substack handles are part of the url, we have to do a different regex for them
  const isValidSubstackHandle = value => {
    const regex = /^[a-zA-Z0-9_]+$/;
    return regex.test(value);
  };

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      name: 'instagramHandle',
      label: 'Instagram Handle',
      value: application.instagramHandle || '',
      isFieldValid: value => !value || isValidSocialMediaHandle(value),
      isValid: true,
      errorMessage: 'Please enter a valid Instagram handle',
      supportText: 'www.instagram.com/',
      inputValueAlignRight: true
    },
    {
      name: 'tiktokHandle',
      label: 'Tiktok Handle',
      value: application.tiktokHandle || '',
      isFieldValid: value => !value || isValidSocialMediaHandle(value),
      isValid: true,
      errorMessage: 'Please enter a valid Tiktok handle',
      supportText: 'www.tiktok.com/@',
      inputValueAlignRight: true
    },
    {
      name: 'youtubeHandle',
      label: 'Youtube Handle',
      value: application.youtubeHandle || '',
      isFieldValid: value => !value || isValidSocialMediaHandle(value),
      isValid: true,
      errorMessage: 'Please enter a valid Youtube Handle',
      supportText: 'www.youtube.com/@',
      inputValueAlignRight: true
    },
    {
      name: 'pinterestHandle',
      label: 'Pinterest Handle',
      value: application.pinterestHandle || '',
      isFieldValid: value => !value || isValidSocialMediaHandle(value),
      isValid: true,
      errorMessage: 'Please enter a valid Pinterest Handle',
      supportText: 'www.pinterest.com/',
      inputValueAlignRight: true
    },
    {
      name: 'substackHandle',
      label: 'Substack Handle',
      value: application.substackHandle || '',
      isFieldValid: value => !value || isValidSubstackHandle(value),
      isValid: true,
      errorMessage: 'Please enter a valid Substack Handle',
      supportText: '.substack.com',
      inputValueAlignRight: false
    },
    {
      name: 'website',
      label: 'Optional: Other Links',
      value: application.website || '',
      isFieldValid: value => !value || isValidUrl(value),
      isValid: true,
      errorMessage: 'Please enter a valid website url',
      supportText: 'https://',
      inputValueAlignRight: true
    },
    {
      name: 'largestFollowingPlatform',
      label: 'Where is your largest following?*',
      value: application.largestFollowingPlatform || '',
      isFieldValid: value => !!value,
      isValid: true,
      errorMessage: 'Please select the platform with your largest following',
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-select-container'>
            <div className='creator-application-input-icon'>
              <FontAwesomeIcon icon={faAngleDown} />
            </div>

            <select
              className={cn('creator-application-input', { invalid: !field.isValid })}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { value }));
                props.updateCreatorApplication({ largestFollowingPlatform: value });
              }}
            >
              <option value=''>Where is your largest following?*</option>
              <option value='Instagram'>Instagram</option>
              <option value='TikTok'>TikTok</option>
              <option value='YouTube'>YouTube</option>
              <option value='Pinterest'>Pinterest</option>
              <option value='Substack'>Substack</option>
              <option value='Other'>Other</option>
            </select>
          </div>
        );
      }
    },
    {
      name: 'largestFollowing',
      label: 'What is your following on that platform?*',
      value: application.largestFollowing || '',
      isFieldValid: value => !!value,
      isValid: true,
      errorMessage: 'Please select the your largest following',
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-select-container'>
            <div className='creator-application-input-icon'>
              <FontAwesomeIcon icon={faAngleDown} />
            </div>

            <select
              className={cn('creator-application-input', { invalid: !field.isValid })}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { value }));
                props.updateCreatorApplication({ largestFollowing: value });
              }}
            >
              <option value=''>What is your following on that platform?*</option>
              <option value='0-250'>0 - 250</option>
              <option value='250-1k'>250 - 1,000</option>
              <option value='1k-10k'>1,000 - 10,000</option>
              <option value='10k-50k'>10,000 - 50,000</option>
              <option value='50k-100k'>50,000 - 100,000</option>
              <option value='100k-500k'>100,000 - 500,000</option>
              <option value='500k-1m'>500,000 - 1,000,000</option>
              <option value='1m+'>1,000,000+</option>
              <option value='Other'>Other</option>
            </select>
          </div>
        );
      }
    }
  ]);

  const handleFields = ['instagramHandle', 'tiktokHandle', 'youtubeHandle', 'pinterestHandle', 'substackHandle'];
  const validateFields = () => {
    // Because they can technically leave all the handles blank, check at least one is filled first
    const atLeastOneHandleIsFilledOut = fields.some(f => handleFields.includes(f.name) && f.value.length > 0);
    if (!atLeastOneHandleIsFilledOut) {
      const newFields = fields.map(f => (handleFields.includes(f.name) ? { ...f, isValid: false } : f));
      setFields(newFields);
      return 'Please enter at least one social media handle';
    }

    let firstErrorMessage = null;
    const newFields = fields.map(field => {
      const isValidField = field.isFieldValid(field.value);
      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });
    setFields(newFields);

    return firstErrorMessage;
  };

  const goToNextTab = async () => {
    const errorMessage = validateFields();
    if (errorMessage) {
      window.__ADD_EVENT__('Creator Application - Go To Next Tab Error', { tab: 'Social Info', error: errorMessage });
      return window.ALERT.error(errorMessage);
    }

    window.__ADD_EVENT__('Creator Application - Go To Next Tab', { tab: 'Social Info' });
    props.goToNextTab();
  };

  useEffect(() => {
    Mousetrap.bind('enter', e => {
      e.preventDefault();
      goToNextTab();
    });

    return () => {
      Mousetrap.unbind('enter');
    };
  }, []);

  useEffect(() => {
    const atLeastOneHandleIsFilledOut = fields.some(f => handleFields.includes(f.name) && f.value.length > 0);
    if (!atLeastOneHandleIsFilledOut) return;

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field.isValid) continue;

      const isValidField = field.isFieldValid(field.value);
      if (isValidField) {
        setFields(fields => {
          const newFields = [...fields];
          newFields[i].isValid = true;
          return newFields;
        });
      }
    }
  }, [fields]);

  return (
    <div className='creator-application-social-info-outer'>
      <ScrollToTop />
      <div className='creator-application-social-info-inner'>
        <div className='creator-application-progress-container'>
          <div className='creator-application-progress-container-circle complete clickable' onClick={() => props.goToPreviousTab()}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className='creator-application-progress-container-circle active'>2</div>
          <div className='creator-application-progress-container-circle clickable' onClick={goToNextTab}>
            3
          </div>
        </div>

        <h1>What are your socials?</h1>
        <h2>If you’re someone who frequently talks about products on your social channels, ShopMy is for you.</h2>

        <div className='creator-application-social-info-form'>
          {fields.map((field, index) => (
            <div key={index} className='creator-application-input-container'>
              <Tooltip message={field.isValid ? '' : field.errorMessage}>
                {field.customComponent ? (
                  field.customComponent(field, index)
                ) : (
                  <input
                    className={cn('creator-application-input', {
                      invalid: !field.isValid,
                      alignRight: field.inputValueAlignRight,
                      filled: !!field.value
                    })} // With this we can delay when we show to the errored state
                    type='text'
                    placeholder={field.label}
                    id={field.name}
                    value={field.value}
                    onChange={e => {
                      const { value } = e.target;
                      setFields(prev => updateField(prev, index, { value }));
                      props.updateCreatorApplication({ [field.name]: value });
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        goToNextTab();
                      }

                      if (e.key === 'Escape') {
                        e.preventDefault();
                        const newFields = [...fields];
                        newFields[index].value = '';
                        setFields(newFields);
                        props.updateCreatorApplication({ [field.name]: '' });
                      }
                    }}
                  />
                )}
              </Tooltip>

              {!!field.supportText && (
                <div className={cn('creator-application-input-support-text', { alignRight: !field.inputValueAlignRight })}>{field.supportText}</div>
              )}
            </div>
          ))}

          <div className='creator-application-social-info-disclaimer'>
            <b style={{ color: 'black' }}>Note:</b> Please be honest, as we fact-check. Follower count is just one factor we consider — our review
            process takes a holistic approach, valuing authenticity, content quality, and engagement.
          </div>

          <div className='creator-application-progress-button-container'>
            <button className='creator-application-progress-button' onClick={() => props.goToPreviousTab()}>
              Back
            </button>
            <button className='creator-application-progress-button primary' onClick={goToNextTab}>
              Next
            </button>
          </div>

          <div className='creator-application-progress-button-disclaimer'>You must enter at least one social account</div>

          <a href='https://shopmy.us/home/brands' className='creator-application-progress-link'>
            I am a Brand
          </a>
        </div>
      </div>
    </div>
  );
};

CreatorApplicationSocialInfo.propTypes = {
  application: PropTypes.object.isRequired,
  updateCreatorApplication: PropTypes.func.isRequired,
  goToNextTab: PropTypes.func.isRequired,
  goToPreviousTab: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  application: state.application
});

export default connect(mapStateToProps, {
  updateCreatorApplication
})(CreatorApplicationSocialInfo);
